import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { AuthContext, type SignUpMetadata } from '@/lib/auth'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false

    // Bootstrap from the persisted session if any. Supabase JS handles
    // localStorage persistence by default; this just pulls the current
    // value into React state.
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session)
      setUser(data.session?.user ?? null)
      setLoading(false)
    })

    // Subscribe to all subsequent auth events (sign in, sign out, token
    // refresh, password reset, user updated).
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setUser(nextSession?.user ?? null)
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      signIn: async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        return { error }
      },
      signUp: async (email: string, password: string, metadata: SignUpMetadata) => {
        // Confirmation link lands on /sign-in?confirmed=1 so SignInPage
        // can show a "Email verified" banner. Supabase's anti-enumeration
        // means the response is fully blanked for both fresh and
        // duplicate signups, so we can't branch UX client-side; routing
        // is universal /verify-email unless a session is returned (=
        // confirmations disabled, auto-signed-in).
        const emailRedirectTo =
          typeof window !== 'undefined'
            ? `${window.location.origin}/sign-in?confirmed=1`
            : undefined
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo,
            // raw_user_meta_data is read by tg_create_user_profile to
            // populate user_profiles + name the auto-created Personal org.
            data: {
              first_name: metadata.first_name,
              last_name: metadata.last_name,
              phone: metadata.phone ?? null,
              company_name: metadata.company_name ?? null,
            },
          },
        })
        return { error, autoSignedIn: data.session != null }
      },
      signInWithMagicLink: async (email: string, next?: string) => {
        // Passwordless sign-in for an EXISTING user — a nice fallback when a
        // password is forgotten. shouldCreateUser:false keeps this strictly
        // sign-in: an unknown email won't silently create an account (the UI
        // shows a generic confirmation either way to avoid leaking which
        // emails are registered).
        //
        // The link lands on `next` when supplied — /sign-in?next=<path> carries
        // a post-auth destination (e.g. an invite URL whose InviteAcceptPage
        // auto-accepts once authenticated). Only same-origin internal paths are
        // honored (must start with a single '/'), otherwise fall back to
        // /account, to avoid turning the emailed link into an open redirect.
        const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/account'
        const emailRedirectTo =
          typeof window !== 'undefined' ? `${window.location.origin}${safeNext}` : undefined
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { shouldCreateUser: false, emailRedirectTo },
        })
        return { error }
      },
      signOut: async () => {
        await supabase.auth.signOut()
      },
      resendVerification: async (email: string) => {
        const emailRedirectTo =
          typeof window !== 'undefined'
            ? `${window.location.origin}/sign-in?confirmed=1`
            : undefined
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo },
        })
        return { error }
      },
      resetPassword: async (email: string) => {
        // Land on the public /reset-password page. The recovery email uses a
        // token_hash link (not a server-verified ConfirmationURL), so no
        // session exists until the user submits a new password there. Using
        // window.location.origin keeps the reset on whichever approved domain
        // the request came from (geoborder.us, geoborder.metaphase.tech, a
        // deploy preview, or localhost).
        const redirectTo =
          typeof window !== 'undefined'
            ? `${window.location.origin}/reset-password`
            : undefined
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
        return { error }
      },
    }),
    [user, session, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
